import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Music2 } from "lucide-react";
import { Reveal } from "./Reveal";
import HeaderBg, { STUDIO } from "./HeaderBg";

export default function Discography({ items = [] }) {
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);

  useEffect(() => {
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    const onEnd = () => setPlayingId(null);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("ended", onEnd);
      a.pause();
    };
  }, []);

  const toggle = (item) => {
    const a = audioRef.current;
    if (!item.preview_url) return;
    if (playingId === item.id) {
      a.pause();
      setPlayingId(null);
    } else {
      a.src = item.preview_url;
      a.play().catch(() => {});
      setPlayingId(item.id);
    }
  };

  return (
    <section id="discography" className="relative bg-[#0d0d0d] py-24 md:py-40" data-testid="discography-section">
      <HeaderBg image={STUDIO.console} />
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24">
          <div className="max-w-2xl">
            <p className="overline text-gold mb-6">The Catalog</p>
            <h2 className="headline text-5xl md:text-7xl text-white">Discography</h2>
          </div>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-6">
          {items.map((a, i) => {
            const isPlaying = playingId === a.id;
            return (
              <motion.div
                key={a.id}
                data-testid={`disc-item-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className="group flex flex-col"
              >
                <div className="relative aspect-square overflow-hidden">
                <img
                  src={a.cover_image || undefined}
                  alt={`${a.title}, ${a.artist}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className={`absolute inset-0 transition-colors duration-500 ${isPlaying ? "bg-black/70" : "bg-black/20 group-hover:bg-black/70"}`} />

                <div className="absolute inset-0 p-4 md:p-5 flex flex-col justify-between">
                  <span
                    className="font-display uppercase text-[0.62rem] tracking-[0.2em] px-2 py-1 w-fit border"
                    style={{ color: a.accent, borderColor: a.accent }}
                  >
                    {a.year}
                  </span>
                  <div className={`transition-all duration-500 ${isPlaying ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100"}`}>
                    <p className="font-editorial italic text-lg md:text-2xl text-white leading-tight mb-1">{a.title}</p>
                    <p className="font-display uppercase text-[0.66rem] tracking-[0.15em] text-white/60">{a.artist}</p>
                  </div>
                </div>

                {a.preview_url && (
                  <button
                    onClick={() => toggle(a)}
                    data-testid={`disc-play-${i}`}
                    aria-label={isPlaying ? "Pause preview" : "Play preview"}
                    className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isPlaying ? "bg-gold text-black opacity-100" : "bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-gold hover:text-black"}`}
                  >
                    {isPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                  </button>
                )}

                {isPlaying && (
                  <div className="absolute bottom-4 right-4 flex items-end gap-[3px] h-4" data-testid="eq-indicator">
                    {[0, 1, 2, 3].map((b) => (
                      <span
                        key={b}
                        className="w-[3px] bg-gold"
                        style={{ animation: `eq 0.8s ${b * 0.12}s ease-in-out infinite alternate`, height: "40%" }}
                      />
                    ))}
                  </div>
                )}
                </div>

                {a.spotify_url && (
                  <a
                    href={a.spotify_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`disc-spotify-${i}`}
                    className="flex items-center justify-between gap-2 mt-2.5 px-0.5 group/sp"
                  >
                    <span className="font-body text-[0.72rem] text-white/45 truncate">{a.title}</span>
                    <span className="flex items-center gap-1.5 font-display uppercase tracking-[0.14em] text-[0.6rem] text-white/50 group-hover/sp:text-[#1DB954] transition-colors shrink-0">
                      <Music2 size={12} /> Spotify
                    </span>
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      <style>{`@keyframes eq { from { height: 20%; } to { height: 100%; } }`}</style>
    </section>
  );
}
